import React, { useImperativeHandle, useState, forwardRef } from 'react';
import apiService from '../../services/api';

interface SchedulingInterfaceProps {
  leadId: string;
  onSchedule?: (result: any) => void;
  showTriggerButton?: boolean;
}

export type SchedulingInterfaceHandle = {
  open: () => void;
  close: () => void;
};

const SchedulingInterface = forwardRef<SchedulingInterfaceHandle, SchedulingInterfaceProps>(({ leadId, onSchedule, showTriggerButton = true }, ref) => {
  const [isOpen, setIsOpen] = useState(false);
  const [formData, setFormData] = useState({
    type: 'call',
    scheduledAt: '',
    duration: 60,
    notes: '',
    location: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useImperativeHandle(ref, () => ({
    open: () => setIsOpen(true),
    close: () => setIsOpen(false),
  }), []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const result = await apiService.scheduleAppointment({
        leadId,
        ...formData,
        scheduledAt: new Date(formData.scheduledAt).toISOString()
      });

      if (onSchedule) {
        onSchedule(result);
      }

      // Reset form
      setFormData({
        type: 'call',
        scheduledAt: '',
        duration: 60,
        notes: '',
        location: ''
      });
      setIsOpen(false);

      // Show success message
      alert(`Appointment scheduled successfully! Notification sent via ${result.channelUsed || 'preferred channel'}`);
    } catch (error) {
      console.error('Failed to schedule appointment:', error);
      alert('Failed to schedule appointment. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'duration' ? parseInt(value) : value
    }));
  };

  if (!isOpen) {
    return (
      <>
        {showTriggerButton && (
          <div className="mb-4">
            <button
              onClick={() => setIsOpen(true)}
              className="px-4 py-2 bg-darkBlue text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              📅 Schedule Appointment
            </button>
          </div>
        )}
      </>
    );
  }

  return (
    <div className="mb-4 mt-[500px] p-4 border border-lightGray rounded-lg bg-lightestGray relative z-10">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-darkestSlate">Schedule Appointment</h3>
        <button
          onClick={() => setIsOpen(false)}
          className="text-gray-500 hover:text-gray-700"
        >
          ✕
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-darkGray mb-1">
            Appointment Type
          </label>
          <select
            name="type"
            value={formData.type}
            onChange={handleInputChange}
            className="w-full px-3 py-2 border border-lightGray rounded-lg focus:border-darkBlue outline-none"
            required
          >
            <option value="call">📞 Phone Call</option>
            <option value="meeting">🤝 Meeting</option>
            <option value="tour">🏠 Property Tour</option>
            <option value="showing">🔑 Showing</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-darkGray mb-1">
            Date & Time
          </label>
          <input
            type="datetime-local"
            name="scheduledAt"
            value={formData.scheduledAt}
            onChange={handleInputChange}
            className="w-full px-3 py-2 border border-lightGray rounded-lg focus:border-darkBlue outline-none"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-darkGray mb-1">
            Duration (minutes)
          </label>
          <select
            name="duration"
            value={formData.duration}
            onChange={handleInputChange}
            className="w-full px-3 py-2 border border-lightGray rounded-lg focus:border-darkBlue outline-none"
          >
            <option value={30}>30 minutes</option>
            <option value={60}>1 hour</option>
            <option value={90}>1.5 hours</option>
            <option value={120}>2 hours</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-darkGray mb-1">
            Location (optional)
          </label>
          <input
            type="text"
            name="location"
            value={formData.location}
            onChange={handleInputChange}
            placeholder="e.g., Office, Property Address, Virtual Meeting"
            className="w-full px-3 py-2 border border-lightGray rounded-lg focus:border-darkBlue outline-none"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-darkGray mb-1">
            Notes (optional)
          </label>
          <textarea
            name="notes"
            value={formData.notes}
            onChange={handleInputChange}
            placeholder="Additional details about the appointment..."
            rows={3}
            className="w-full px-3 py-2 border border-lightGray rounded-lg focus:border-darkBlue outline-none"
          />
        </div>

        <div className="flex gap-2">
          <button
            type="submit"
            disabled={isSubmitting}
            className="flex-1 px-4 py-2 bg-darkBlue text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isSubmitting ? 'Scheduling...' : 'Schedule Appointment'}
          </button>
          <button
            type="button"
            onClick={() => setIsOpen(false)}
            className="px-4 py-2 border border-lightGray text-darkGray rounded-lg hover:bg-lightGray transition-colors"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
});

export default SchedulingInterface;
